import type { BakedStaticsManifest } from './bakedManifest';
import staticsManifestUrl from '../../assets/baked/statics/statics.json?url';

/**
 * Fetch-once store for the baked statics manifest.
 *
 * The manifest grew past 650 KB with the classic dungeon bakes, so importing the
 * JSON statically inlined it into the cold entry script and blew the gzip budget
 * the postbuild gate enforces (ADR-0310, issue #846). Keeping only its URL in
 * JavaScript makes the manifest an independently cached JSON resource fetched in
 * parallel with the statics atlas, the same treatment the sprite manifests got
 * in `LazyBakedManifestStore`.
 *
 * Boot awaits {@link loadStaticsManifest} (via `loadStaticTextures`) before any
 * game UI exists, so synchronous consumers such as `cardActionArt` can read
 * {@link staticsManifestIfLoaded} safely afterwards.
 */
let cached: BakedStaticsManifest | undefined;
let pending: Promise<BakedStaticsManifest> | undefined;

async function fetchStaticsManifest(): Promise<BakedStaticsManifest> {
  const response = await fetch(staticsManifestUrl);
  if (!response.ok) {
    throw new Error(`statics manifest ${response.status}: ${staticsManifestUrl}`);
  }
  cached = (await response.json()) as BakedStaticsManifest;
  return cached;
}

/** Load (once) the baked statics manifest as a fetched JSON resource. */
export function loadStaticsManifest(): Promise<BakedStaticsManifest> {
  pending ??= fetchStaticsManifest();
  return pending;
}

/** The manifest when it has finished loading; boot awaits the load before UI exists. */
export function staticsManifestIfLoaded(): BakedStaticsManifest | undefined {
  return cached;
}

/** Test seam: node test environments have no fetch target, so seed the manifest directly. */
export function seedStaticsManifest(manifest: BakedStaticsManifest): void {
  cached = manifest;
  pending = Promise.resolve(manifest);
}
